import { useEffect, useState } from 'react';
import { useLazyGetPreferencesQuery, useUpdatePreferencesMutation } from '@/store/api/userApi';
import { diagnosticApi } from '@/store/api/diagnosticApi';
import { setNotificationFeatureFlag } from '@/store/slices/applicationSlice';
import { useDispatch } from 'react-redux';
import { useToast } from '@/hooks/shared/use-toast';

export const usePreferences = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [fetchUserPreferences, { data: preferencesData, isLoading, isFetching }] = useLazyGetPreferencesQuery();
  const [updatePreferences, { isLoading: isUpdating }] = useUpdatePreferencesMutation();

  const { notifications_enabled: notificationsEnabled, minimum_publish_status: minimumPublishStatus } =
    preferencesData || {};

  const [minimumPublishStatusState, setMinimumPublishStatusState] = useState<number | undefined>(undefined);
  const [notificationsEnabledState, setNotificationsEnabledState] = useState<boolean | undefined>(undefined);

  const savePreferences = async () => {
    try {
      await updatePreferences({
        minimum_publish_status: minimumPublishStatusState,
        notifications_enabled: notificationsEnabledState,
      }).unwrap();

      dispatch(diagnosticApi.util.resetApiState());
      dispatch(setNotificationFeatureFlag(notificationsEnabledState));

      toast({
        variant: 'success',
        title: 'Success',
        description: 'Preferences have been updated',
      });
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: `Failed to update preferences`,
      });
    }
  };

  useEffect(() => {
    fetchUserPreferences({});
  }, []);

  useEffect(() => {
    if (preferencesData) {
      setMinimumPublishStatusState(minimumPublishStatus);
      setNotificationsEnabledState(notificationsEnabled);
    }
  }, [preferencesData, minimumPublishStatus, notificationsEnabled]);

  return {
    minimumPublishStatus: minimumPublishStatusState,
    notificationsEnabled: notificationsEnabledState,
    setMinimumPublishStatus: setMinimumPublishStatusState,
    setNotificationsEnabled: setNotificationsEnabledState,
    savePreferences,
    isLoadingPreferences: isLoading || isFetching,
    isUpdatingPreferences: isUpdating,
  };
};
